import { Injectable, inject } from '@angular/core';
import { SubscriptionApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/subscription/subscription-api';
import { WebViewBridgeService } from '../webview/webview-bridge.service';
import { WebViewAction } from '../../enums/webview-action';
import { PosthogService } from './posthog.service';

export type CheckoutPlan = 'MONTHLY' | 'YEARLY';

/**
 * Contrat d'un moyen de paiement.
 *
 * Cette interface existe pour une raison précise : si Apple ou Google exigent
 * un jour l'achat in-app, on écrit un `IapCheckoutProvider` qui l'implémente et
 * AUCUN écran ne change. Le bouton d'achat, lui, vit dans l'app web : on peut
 * donc basculer sans passer par une release des stores.
 */
export interface CheckoutProvider {
  /** Ouvre le tunnel d'achat. Résout quand le tunnel est OUVERT, pas payé. */
  purchase(plan: CheckoutPlan): Promise<void>;
  /** Factures et moyen de paiement. */
  manageBilling(): Promise<void>;
  /** Ce moyen de paiement est-il utilisable dans le contexte courant ? */
  isAvailable(): boolean;
}

/**
 * Paiement par Stripe, ouvert dans le NAVIGATEUR EXTERNE.
 *
 * ⚠️ ATTENTION — ce n'est PAS une voie conforme, contrairement à ce que ce
 * commentaire affirmait. Le « modèle Spotify » repose sur l'exception des
 * *reader apps* (règle 3.1.3(a) : presse, musique, vidéo, livres), à laquelle
 * Vegiwise ne peut pas prétendre. Pour une app ordinaire, un bouton qui mène à
 * un paiement extérieur est de l'ORIENTATION, que la règle 3.1.3 interdit —
 * sauf ouvertures récentes propres aux États-Unis et à l'Europe, qui
 * demandent des autorisations spécifiques.
 *
 * Sortir la transaction de la WebView évite le pire (une transaction tierce
 * exécutée DANS l'app), mais ne met pas à l'abri d'un refus ni d'un retrait.
 * La sortie durable est l'achat intégré : `CheckoutProvider` existe pour qu'un
 * `IapCheckoutProvider` prenne cette place sans qu'aucun écran ne change.
 *
 * Voir `analyses/product/achat-integre/`.
 */
@Injectable({ providedIn: 'root' })
export class CheckoutService implements CheckoutProvider {
  private readonly subscriptionApi = new SubscriptionApi();
  private readonly webViewBridge = inject(WebViewBridgeService);
  private readonly posthog = inject(PosthogService);

  /** Un achat a été lancé : sert à resynchroniser au retour dans l'app. */
  private purchaseInFlight = false;

  isAvailable(): boolean {
    return true;
  }

  async purchase(plan: CheckoutPlan): Promise<void> {
    // Les navigateurs mobiles n'autorisent `window.open` que pendant la partie
    // SYNCHRONE du geste utilisateur : on pré-ouvre l'onglet AVANT tout await,
    // et on y pose l'URL ensuite. Sans cela, le bloqueur de fenêtres surgissantes
    // avale le tunnel de paiement.
    const pendingTab = this.hasNativeChannel()
      ? null
      : window.open('', '_blank');

    try {
      const { url } = await this.subscriptionApi.createCheckoutSession(plan);
      this.purchaseInFlight = true;
      this.posthog.capture('checkout_started', { plan });
      this.openExternal(url, pendingTab);
    } catch (error) {
      pendingTab?.close();
      throw error;
    }
  }

  async manageBilling(): Promise<void> {
    const pendingTab = this.hasNativeChannel()
      ? null
      : window.open('', '_blank');
    try {
      const { url } = await this.subscriptionApi.createPortalSession();
      this.openExternal(url, pendingTab);
    } catch (error) {
      pendingTab?.close();
      throw error;
    }
  }

  /** Un achat vient-il d'être lancé ? (consommé une seule fois) */
  consumePurchaseInFlight(): boolean {
    const value = this.purchaseInFlight;
    this.purchaseInFlight = false;
    return value;
  }

  private hasNativeChannel(): boolean {
    return (
      this.webViewBridge.isChannelAvailable(WebViewAction.openExternalUrl) ||
      this.webViewBridge.isChannelAvailable(WebViewAction.openOAuthUrl)
    );
  }

  /**
   * Ouvre dans le navigateur EXTERNE. Cascade identique à celle du renvoi vers
   * la boutique : canal dédié → canal OAuth (indispensable, le build actuel des
   * stores ne connaît pas encore `openExternalUrl`) → onglet pré-ouvert → dernier
   * recours.
   */
  private openExternal(url: string, pendingTab: Window | null): void {
    if (this.webViewBridge.isChannelAvailable(WebViewAction.openExternalUrl)) {
      this.webViewBridge.postMessageToChannel(
        WebViewAction.openExternalUrl,
        url,
      );
    } else if (
      this.webViewBridge.isChannelAvailable(WebViewAction.openOAuthUrl)
    ) {
      this.webViewBridge.postMessageToChannel(WebViewAction.openOAuthUrl, url);
    } else if (pendingTab && !pendingTab.closed) {
      pendingTab.location.href = url;
    } else {
      window.open(url, '_blank');
    }
  }
}
